import { cloneElement, Dispatch, SetStateAction, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import styled from 'styled-components';

import {
  selectIsEmbeddedMessagingCoreFeatureFlagOn,
  selectUserRbacPermissions,
  selectIsReactImportEmailTemplateFlagOn,
} from 'core/userContext';
import { useLocaleSettingsContext } from 'modules/campaigns/lib/contexts/LocaleSettingsContext';
import { FolderId } from 'modules/domains/foldering';
import { BUTTON_SIZES, PrimaryButton } from 'ui/button';
import { PageHeader } from 'ui/pageHeader';
import { Tooltip } from 'ui/tooltip';
import { AddNew16, ChevronDown16 } from 'ui/icons';
import { ALIGNMENT, Menu } from 'ui/menu';
import { spacing } from 'ui/theme';
import { useWhatsAppMessageChannelFeature } from 'utils/hooks/featureFlags';
import { PROJECT_PERMISSIONS } from 'utils/permissions';
import {
  TEMPLATE_MENU_OPTIONS,
  TEMPLATE_MENU_OPTIONS_KEYS,
  TemplateMenuOptionType,
} from '../../constants';
import { NewTemplateModal } from '../../modals/NewTemplateModal';
import { ImportTemplateButton } from '../ImportTemplateButton/ImportTemplateButton';

const StyledPrimaryButton = styled(PrimaryButton)`
  margin-left: ${spacing.half};
`;

export type TemplateIndexHeaderProps = {
  content: {
    disabledNewTemplateTooltip: string;
    importTemplate: string;
    newTemplate: string;
    title: string;
  };
  currentFolderId: FolderId | null;
};

/** add function to be able to control ability to close menu when clicking outside under the menu item level */
export function addShouldCloseOutsidePropToMenuItemContent(
  menuOptions: Record<TEMPLATE_MENU_OPTIONS_KEYS, TemplateMenuOptionType>,
  setShouldCloseMainMenuOutside: Dispatch<SetStateAction<boolean>>,
): Record<TEMPLATE_MENU_OPTIONS_KEYS, TemplateMenuOptionType> {
  return Object.entries(menuOptions).reduce(
    (acc, [key, option]) => {
      acc[key as TEMPLATE_MENU_OPTIONS_KEYS] = {
        ...option,
        content: cloneElement(option.content, { setShouldCloseMainMenuOutside }),
      };
      return acc;
    },
    {} as Record<TEMPLATE_MENU_OPTIONS_KEYS, TemplateMenuOptionType>,
  );
}

export const TemplateIndexHeader = ({ content, currentFolderId }: TemplateIndexHeaderProps) => {
  const isReactImportTemplateFFOn = useSelector(selectIsReactImportEmailTemplateFlagOn);
  const { isWhatsAppEnabled, isWhatsAppGAEnabled } = useWhatsAppMessageChannelFeature();
  const permissions = useSelector(selectUserRbacPermissions);
  const isEmbeddedMessagingCoreEnabled = useSelector(selectIsEmbeddedMessagingCoreFeatureFlagOn);
  const { locales } = useLocaleSettingsContext();
  const defaultLocale = locales[0];
  const [newTemplateModalID, setNewTemplateModalID] = useState<string>('');
  const [shouldCloseOutside, setShouldCloseOutside] = useState<boolean>(true);
  const isNewTemplateButtonDisabled = useMemo(
    () => !permissions.includes(PROJECT_PERMISSIONS.ManageBaseTemplates),
    [permissions],
  );

  const templateMenuOptionsWithClickOutsideControl = addShouldCloseOutsidePropToMenuItemContent(
    TEMPLATE_MENU_OPTIONS,
    setShouldCloseOutside,
  );

  const newTemplateMenuOptions = Object.values(templateMenuOptionsWithClickOutsideControl)
    .filter((option) =>
      !isEmbeddedMessagingCoreEnabled ? option.id !== TEMPLATE_MENU_OPTIONS.embedded.id : option,
    )
    .filter((option) =>
      !isWhatsAppEnabled ? option.id !== TEMPLATE_MENU_OPTIONS.whatsApp.id : option,
    )
    .map((option) => ({
      // removes label from the menu options so content will be used
      id: option.id,
      modalLabel: option.modalLabel,
      slug: option.slug,
      content: option.content,
    }));

  const handleNewTemplateModalId = (selectedId: string) => {
    if (
      isWhatsAppEnabled &&
      isWhatsAppGAEnabled &&
      selectedId === TEMPLATE_MENU_OPTIONS_KEYS.WhatsApp &&
      !defaultLocale?.mappedWhatsAppInfobipLanguage
    ) {
      return;
    }
    setNewTemplateModalID(selectedId);
    setShouldCloseOutside(true);
  };

  return (
    <PageHeader
      data-test="template-index-page-header"
      pageActions={
        <>
          <ImportTemplateButton
            isReactFFOn={isReactImportTemplateFFOn}
            text={content.importTemplate}
          />
          <Menu
            id="new-template-menu"
            handleSelectionChange={handleNewTemplateModalId}
            horizontalAlignment={ALIGNMENT.RIGHT}
            isDisabled={isNewTemplateButtonDisabled}
            options={newTemplateMenuOptions}
            closeOnClickOutside={shouldCloseOutside}
          >
            <Tooltip
              isEnabled={isNewTemplateButtonDisabled}
              tooltipContent={content.disabledNewTemplateTooltip}
            >
              <StyledPrimaryButton
                data-test="new-template-button"
                isDisabled={isNewTemplateButtonDisabled}
                size={BUTTON_SIZES.LARGE}
              >
                <AddNew16 data-test="new-template-add-icon" />
                <span data-test="new-template-label">{content.newTemplate}</span>
                <ChevronDown16 data-test="new-template-icon" />
              </StyledPrimaryButton>
            </Tooltip>
          </Menu>
          {newTemplateModalID && (
            <NewTemplateModal
              data-test="new-template-modal"
              id={newTemplateModalID}
              closeModal={() => setNewTemplateModalID('')}
              currentFolderId={currentFolderId}
            />
          )}
        </>
      }
    >
      {content.title}
    </PageHeader>
  );
};
